import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../../../common/models/api-response.model';
import { Insight } from '../../../../common/models/insight.model';
import { CreatedRequest } from '../../../../common/models/created-by-me.model';
import { MyInboxService } from '../../../../common/services/my-inbox/my-inbox.service';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { ENDPOINTS } from '../../../../common/constants/endpoints.constants';
import { VENDOR_ROUTES } from '../../../../common/constants/vendor.routing.constants';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import {
  CreatedTasksAdapter,
  CreatedTasksSort,
  CreatedTasksUiFeatures,
} from './created-tasks.adapter';

@Injectable({ providedIn: 'root' })
export class VendorCreatedTasksAdapter implements CreatedTasksAdapter {
  private readonly myInboxService = inject(MyInboxService);
  private readonly tenantService = inject(TenantService);

  readonly uiFeatures: CreatedTasksUiFeatures = {
    showResourceTypeFilter: false,
    showProjectLink: false,
  };

  private readonly vendorEndpoints = {
    flows: ENDPOINTS.VendorPortal.Inbox.Flows,
    insights: ENDPOINTS.VendorPortal.Inbox.FlowInsights,
    export: ENDPOINTS.VendorPortal.Inbox.ExportFlows,
    archive: ENDPOINTS.VendorPortal.Flow.Archive,
    restore: ENDPOINTS.VendorPortal.Flow.Restore,
    archived: ENDPOINTS.VendorPortal.Flow.Archived,
  };

  getTasks(
    page: number,
    perPage: number,
    filter: string,
    sort: CreatedTasksSort,
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<ApiResponse<CreatedRequest>> {
    return this.myInboxService.getFlowsCreatedByMe(
      page,
      perPage,
      filter,
      sort,
      search,
      additionalFilters,
      { url: this.vendorEndpoints.flows }
    );
  }

  getArchiveTasks(
    page: number,
    perPage: number,
    sort: CreatedTasksSort,
    search: string,
    additionalFilters?: Record<string, string | string[]>
  ): Observable<ApiResponse<CreatedRequest>> {
    return this.myInboxService.getFilteredArchive(
      page,
      perPage,
      'created_by_me',
      sort,
      0,
      1,
      search,
      additionalFilters,
      'project',
      { url: this.vendorEndpoints.archived }
    );
  }

  getInsights(): Observable<{ data: Insight[] }> {
    return this.myInboxService.getFlowsCreatedByMeInsights({
      url: this.vendorEndpoints.insights,
    });
  }

  exportTasks(form: Record<string, unknown>): Observable<unknown> {
    return this.myInboxService.exportCreatedByMe(form, {
      url: this.vendorEndpoints.export,
    });
  }

  archiveRequest(workflowId: number): Observable<unknown> {
    return this.myInboxService.archiveRequest(workflowId, {
      url: this.vendorEndpoints.archive,
    });
  }

  restoreRequest(workflowId: number): Observable<unknown> {
    return this.myInboxService.restoreRequest(workflowId, {
      url: this.vendorEndpoints.restore,
    });
  }

  getRequestViewLink(flowId: number): string {
    return this.tenantService.vendorPath(
      `${VENDOR_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${flowId}`
    );
  }

  getRelatedRequestViewLink(childFlowId: number): string {
    return this.getRequestViewLink(childFlowId);
  }
}
